import { useState, useEffect, useCallback } from 'react';
import {
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, communityApi, isAssistantCommunitySuccess } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import type { CommentResponseDto, CommentStatus } from '../types/assistantCommunity';

const STATUS_LABELS: Record<CommentStatus, string> = {
  Draft: 'مسودة',
  Pending: 'قيد المراجعة',
  Approved: 'معتمد',
  Rejected: 'مرفوض',
};

const STATUS_COLORS: Record<CommentStatus, string> = {
  Draft: 'bg-gray-100 text-gray-800',
  Pending: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};

interface SubjectOption {
  id: string;
  name: string;
}

const Community = () => {
  const { isModerator, isTeacher } = useUser();
  const canModerate = isModerator();
  const canReplyAsTeacher = isTeacher();

  const [comments, setComments] = useState<CommentResponseDto[]>([]);
  const [subjects, setSubjects] = useState<SubjectOption[]>([]);
  const [statusFilter, setStatusFilter] = useState<CommentStatus | ''>(() =>
    isModerator() ? '' : 'Approved'
  );
  const [subjectFilter, setSubjectFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [replyingId, setReplyingId] = useState<string | null>(null);

  const fetchSubjects = async () => {
    try {
      const response = await authFetch(apiUrl('Subject'), {
        headers: { Authorization: `Bearer ${authService.getToken()}` },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setSubjects(data.data.map((s: { id: string; name: string }) => ({ id: s.id, name: s.name })));
      }
    } catch {
      console.error('Failed to load subjects');
    }
  };

  const fetchComments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filter = {
        page: currentPage,
        pageSize,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(subjectFilter ? { subjectId: subjectFilter } : {}),
      };

      const result = canModerate
        ? await communityApi.getAssistantComments(filter)
        : await communityApi.getTeacherComments(filter);

      setComments(result.comments || []);
      setTotalCount(result.totalCount ?? 0);
      setTotalPages(result.totalPages ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تحميل التعليقات');
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, subjectFilter, canModerate]);

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleStatusChange = async (commentId: string, status: CommentStatus) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await communityApi.changeCommentStatus(commentId, status);
      if (!isAssistantCommunitySuccess(result)) {
        throw new Error(String(result.message || result.data || 'فشل تغيير الحالة'));
      }
      showSuccess(`تم تحديث الحالة إلى ${STATUS_LABELS[status]}`);
      await fetchComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التعليق؟')) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await communityApi.deleteComment(commentId);
      if (!isAssistantCommunitySuccess(result)) {
        throw new Error(String(result.message || result.data || 'فشل الحذف'));
      }
      showSuccess('تم حذف التعليق');
      await fetchComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReply = async (commentId: string) => {
    const text = replyText[commentId]?.trim();
    if (!text) return;
    setReplyingId(commentId);
    setError(null);
    try {
      const result = canModerate
        ? await communityApi.createAssistantReply(commentId, text)
        : await communityApi.createTeacherReply(commentId, text);
      if (!isAssistantCommunitySuccess(result)) {
        throw new Error(String(result.message || result.data || 'فشل إرسال الرد'));
      }
      showSuccess('تم إرسال الرد بنجاح');
      setReplyText((prev) => ({ ...prev, [commentId]: '' }));
      await fetchComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ');
    } finally {
      setReplyingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('ar-EG');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-semibold text-gray-900">المنتدى</h1>
          <p className="mt-1 text-sm text-gray-500">
            {canModerate
              ? 'مراجعة تعليقات الطلاب والموافقة عليها أو رفضها'
              : 'عرض التعليقات المعتمدة والرد على الطلاب'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as CommentStatus | '');
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md py-2 px-3 text-sm"
            >
              <option value="">الكل</option>
              <option value="Draft">مسودة</option>
              <option value="Pending">قيد المراجعة</option>
              <option value="Approved">معتمد</option>
              <option value="Rejected">مرفوض</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">المادة</label>
            <select
              value={subjectFilter}
              onChange={(e) => {
                setSubjectFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md py-2 px-3 text-sm min-w-[160px]"
            >
              <option value="">كل المواد</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => fetchComments()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
          >
            تحديث
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="rounded-md bg-green-50 p-4 mb-4">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {isLoading && comments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">جاري التحميل...</div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow-sm">
            لا توجد تعليقات
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{comment.studentName}</span>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[comment.status]}`}>
                          {STATUS_LABELS[comment.status]}
                        </span>
                      </div>
                      <p className="mt-2 text-gray-700 whitespace-pre-wrap">{comment.text}</p>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                        {comment.subjectName && <span>المادة: {comment.subjectName}</span>}
                        {comment.courseName && <span>الباقة: {comment.courseName}</span>}
                        {comment.unitName && <span>الوحدة: {comment.unitName}</span>}
                        {comment.lessonName && <span>الدرس: {comment.lessonName}</span>}
                        <span>{formatDate(comment.createdAt)}</span>
                      </div>
                    </div>

                    {canModerate && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {comment.status !== 'Approved' && (
                          <button
                            onClick={() => handleStatusChange(comment.id, 'Approved')}
                            disabled={isLoading}
                            title="موافقة"
                            className="p-2 text-green-600 hover:bg-green-50 rounded-md disabled:opacity-50"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                        )}
                        {comment.status !== 'Rejected' && (
                          <button
                            onClick={() => handleStatusChange(comment.id, 'Rejected')}
                            disabled={isLoading}
                            title="رفض"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(comment.id)}
                          disabled={isLoading}
                          title="حذف"
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-md disabled:opacity-50"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {comment.replies.length > 0 && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <button
                        type="button"
                        onClick={() => setExpandedId(expandedId === comment.id ? null : comment.id)}
                        className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
                        {comment.replies.length} رد
                        {expandedId === comment.id ? ' ▲' : ' ▼'}
                      </button>
                      {expandedId === comment.id && (
                        <div className="mt-3 space-y-3 pr-4 border-r-2 border-indigo-100">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="text-sm">
                              <div className="font-medium text-gray-800">
                                {reply.replierName || 'مجهول'}
                                <span className="text-gray-400 font-normal mr-1">
                                  ({reply.repliedBy === 'Teacher'
                                    ? 'معلم'
                                    : reply.repliedBy === 'Assistant'
                                      ? 'مساعد'
                                      : reply.repliedBy === 'Moderator'
                                        ? 'مشرف'
                                        : '—'})
                                </span>
                              </div>
                              <p className="text-gray-600 mt-1">{reply.text}</p>
                              <span className="text-xs text-gray-400">{formatDate(reply.createdAt)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {comment.status === 'Approved' && (canModerate || canReplyAsTeacher) && (
                    <div className="mt-4 flex gap-2">
                      <input
                        type="text"
                        value={replyText[comment.id] || ''}
                        onChange={(e) =>
                          setReplyText((prev) => ({ ...prev, [comment.id]: e.target.value }))
                        }
                        placeholder="اكتب ردك..."
                        className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleReply(comment.id);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleReply(comment.id)}
                        disabled={replyingId === comment.id || !replyText[comment.id]?.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm disabled:opacity-50"
                      >
                        {replyingId === comment.id ? '...' : 'رد'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-gray-500">
              إجمالي {totalCount} تعليق — صفحة {currentPage} من {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                السابق
              </button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Community;
